import type { DatalayerOutput, GDDLEvent } from '@analytics/models';
import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  inject,
  Injectable,
  RendererFactory2,
} from '@angular/core';
import { ConfigLoaderService } from '@core/config-angular';
import { PlatformService } from '@core/platform';
import { DomService, ScriptInjectOn } from '@innogy/utils-dom';
import { bufferIf } from '@innogy/utils-rxjs';
import { BehaviorSubject, map, Subject, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdobeLaunchService implements DatalayerOutput {
  readonly #renderer = inject(RendererFactory2).createRenderer(null, null);
  readonly #document = inject(DOCUMENT);
  readonly #destroyRef = inject(DestroyRef);
  readonly #config = inject(ConfigLoaderService);
  readonly #domService = inject(DomService);
  readonly #platform = inject(PlatformService);

  // Subject containing a flag whether or not Adobe Launch has triggered a 'loaded' event.
  readonly launchLoaded$ = new BehaviorSubject<boolean>(false);
  // Subject containing an internal stream of all forwarded events to buffer and fire.
  readonly #inputStream$ = new Subject<GDDLEvent>();

  readonly id = 'ADOBE_LAUNCH';

  dispatchEvent(event: GDDLEvent) {
    this.#inputStream$.next(event);
  }

  outputConditionsAreMet() {
    return this.#platform.isClient();
  }

  async init() {
    await this.#loadAdobeLaunchSDK();
    this.#setupLaunchLoadedListener();
    const subscription = this.#setupEventBuffers().subscribe();
    /* istanbul ignore next */
    this.#destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  #setupEventBuffers() {
    // Buffer events until we have received a 'go' from Adobe Launch.
    const bufferCriterium$ = this.launchLoaded$.pipe(map((loaded) => !loaded));
    const bufferedStream$ = bufferIf(this.#inputStream$, bufferCriterium$);
    return bufferedStream$.pipe(
      tap((event) => {
        // We dispatch a CustomEvent with the event type equal to the `event` property of the GDDL event.
        // Adobe Launch is configured to pick up these Custom Events and process them.
        this.#document.body.dispatchEvent(
          new CustomEvent(event.event, {
            detail: event,
            bubbles: true,
          })
        );
      })
    );
  }

  /**
   * Sets up an event listener that goes off when Adobe Launch sends a `LaunchCoreLoaded` event.
   * This event is
   */
  #setupLaunchLoadedListener() {
    const listenerTeardown = this.#renderer.listen(
      'body',
      'LaunchCoreLoaded',
      () => {
        this.launchLoaded$.next(true);
      }
    );
    /* istanbul ignore next */
    this.#destroyRef.onDestroy(() => listenerTeardown());
  }

  /**
   * Loads the adobe lanuch script by injecting it into the DOM.
   * @see https://experienceleague.adobe.com/en/docs/experience-platform/tags/client-side/asynchronous-deployment
   */
  async #loadAdobeLaunchSDK() {
    const config = await this.#config.getConfigAsync();

    const { enabled, url } = config.adobeLaunch ?? {};

    if (enabled && url) {
      this.#domService.injectScript({
        src: url,
        async: true,
        injectOnPlatform: ScriptInjectOn.CLIENT_ONLY,
        target: 'body',
      });
    }
  }
}
